import { useEffect, useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, useSearchParams } from 'react-router-dom';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { CursorPagination } from '../components/CursorPagination';
import { EmptyState, ErrorState, KpiCard, LoadingState, PageHeader } from '../components/ui';
import { useCursorPagination } from '../hooks/useCursorPagination';
import { copy, formatDisplayName } from '../lib/copy';
import { adminApi, type UsageLedgerItem } from '../lib/api';
import { downloadCsv } from '../lib/csv';
import { useProductScope } from '../context/ProductScopeContext';
import { USAGE_SOURCE_OPTIONS, usageSourceLabel } from '../lib/usage-sources';
import { formatDate, formatNumber, formatPercent, formatRubPrecise } from '../lib/utils';

const PERIOD_OPTIONS = [
  { value: 7, label: '7 дней' },
  { value: 30, label: '30 дней' },
  { value: 90, label: '90 дней' },
];

function exportUsageLedgerCsv(items: UsageLedgerItem[]) {
  const header = [
    'id',
    'created_at',
    'user_id',
    'user_name',
    'model_id',
    'source',
    'input_tokens',
    'output_tokens',
    'cost_rub',
    'credits_charged',
    'fair_use_used',
    'revenue_rub',
    'margin_rub',
    'margin_percent',
  ];
  const rows = items.map((row) => [
    row.id,
    row.created_at,
    row.user_id,
    row.user_name ?? '',
    row.model_id,
    row.source,
    String(row.input_tokens),
    String(row.output_tokens),
    String(row.cost_rub),
    String(row.credits_charged),
    String(row.fair_use_used),
    String(row.revenue_rub),
    String(row.margin_rub),
    String(row.margin_percent),
  ] as string[]);
  downloadCsv('usage-ledger.csv', header, rows);
}

export function UsageAnalyticsPage() {
  const scope = useProductScope();
  const [searchParams] = useSearchParams();
  const [days, setDays] = useState(30);
  const [modelId, setModelId] = useState('');
  const [source, setSource] = useState('');
  const [userId, setUserId] = useState(searchParams.get('user_id') ?? '');
  const pagination = useCursorPagination();

  const filters = useMemo(
    () => ({
      days,
      model_id: modelId.trim() || undefined,
      source: source.trim() || undefined,
      user_id: userId.trim() || undefined,
    }),
    [days, modelId, source, userId],
  );

  const usageApi = useMemo(() => {
    if (scope === 'studio') {
      return {
        overview: adminApi.studioUsageOverview,
        byModel: adminApi.studioUsageByModel,
        timeseries: adminApi.studioUsageTimeseries,
        ledger: adminApi.studioUsageLedger,
      };
    }
    return {
      overview: adminApi.appUsageOverview,
      byModel: adminApi.appUsageByModel,
      timeseries: adminApi.appUsageTimeseries,
      ledger: adminApi.appUsageLedger,
    };
  }, [scope]);

  const overviewQuery = useQuery({
    queryKey: ['usage', scope, 'overview', filters],
    queryFn: () => usageApi.overview(filters),
  });

  const byModelQuery = useQuery({
    queryKey: ['usage', scope, 'by-model', filters],
    queryFn: () => usageApi.byModel(filters),
  });

  const timeseriesQuery = useQuery({
    queryKey: ['usage', scope, 'timeseries', filters],
    queryFn: () => usageApi.timeseries(filters),
  });

  const ledgerQuery = useQuery({
    queryKey: ['usage', scope, 'ledger', filters, pagination.page, pagination.cursor],
    queryFn: () =>
      usageApi.ledger({
        ...filters,
        cursor: pagination.cursor,
        limit: pagination.pageSize,
      }),
  });

  useEffect(() => {
    pagination.reset();
  }, [days, modelId, source, userId, pagination.reset]);

  const isLoading =
    overviewQuery.isLoading || byModelQuery.isLoading || timeseriesQuery.isLoading;
  const isError = overviewQuery.isError || byModelQuery.isError || timeseriesQuery.isError;

  const reload = () => {
    void overviewQuery.refetch();
    void byModelQuery.refetch();
    void timeseriesQuery.refetch();
    void ledgerQuery.refetch();
  };

  const overview = overviewQuery.data;
  const byModel = byModelQuery.data?.items ?? [];
  const chartData =
    timeseriesQuery.data?.items.map((row) => ({
      day: row.day.slice(5),
      cost_rub: row.cost_rub,
      revenue_rub: row.revenue_rub,
      margin_rub: row.margin_rub,
    })) ?? [];

  return (
    <div>
      <PageHeader
        title={copy.nav.usage}
        description="Реальные токены, себестоимость провайдера и маржа по кредитам."
      />

      <div className="ops-card mb-4 grid gap-3 md:grid-cols-4">
        <label className="text-sm">
          <span className="mb-1 block text-ops-muted">Период</span>
          <select
            className="ops-input w-full"
            value={days}
            onChange={(event) => setDays(Number(event.target.value))}
          >
            {PERIOD_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-ops-muted">Модель</span>
          <input
            className="ops-input w-full"
            value={modelId}
            onChange={(event) => setModelId(event.target.value)}
            placeholder="gpt-image-2"
          />
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-ops-muted">Источник</span>
          <select
            className="ops-input w-full"
            value={source}
            onChange={(event) => setSource(event.target.value)}
          >
            {USAGE_SOURCE_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </label>
        <label className="text-sm">
          <span className="mb-1 block text-ops-muted">User ID</span>
          <input
            className="ops-input w-full"
            value={userId}
            onChange={(event) => setUserId(event.target.value)}
            placeholder="uuid"
          />
        </label>
      </div>

      {isLoading ? <LoadingState label="Загрузка аналитики..." /> : null}
      {isError ? <ErrorState message={copy.errors.loadUsage} onRetry={reload} /> : null}

      {overview ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-4">
            <KpiCard
              label="Событий"
              value={formatNumber(overview.totals.events)}
              hint={`с ${formatDate(overview.since)}`}
            />
            <KpiCard
              label="Токены in / out"
              value={`${formatNumber(overview.totals.input_tokens)} / ${formatNumber(overview.totals.output_tokens)}`}
            />
            <KpiCard
              label="COGS (провайдер)"
              value={formatRubPrecise(overview.totals.cost_rub)}
            />
            <KpiCard
              label="Выручка (кредиты)"
              value={formatRubPrecise(overview.totals.revenue_rub)}
              hint={`1 кред. = ${formatRubPrecise(overview.baseline_cost_rub)}`}
            />
          </div>

          <div className="mt-4 grid gap-4 sm:grid-cols-2">
            <KpiCard
              label="Маржа"
              value={formatRubPrecise(overview.totals.margin_rub)}
              hint={formatPercent(overview.totals.margin_percent)}
            />
            <KpiCard
              label="Списано кредитов"
              value={formatNumber(overview.totals.credits_charged)}
              hint={`Fair use: ${formatNumber(overview.totals.fair_use_used)}`}
            />
          </div>

          <div className="ops-card mt-6">
            <h2 className="mb-4 text-sm font-semibold">COGS vs выручка по дням</h2>
            {chartData.length > 0 ? (
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#e5e7eb" />
                    <XAxis dataKey="day" tick={{ fontSize: 12 }} />
                    <YAxis tick={{ fontSize: 12 }} />
                    <Tooltip
                      formatter={(value: number) => formatRubPrecise(value)}
                      contentStyle={{
                        background: '#ffffff',
                        border: '1px solid #e5e7eb',
                        borderRadius: 8,
                      }}
                    />
                    <Legend />
                    <Bar dataKey="cost_rub" name="COGS" fill="#b45309" radius={[4, 4, 0, 0]} />
                    <Bar dataKey="revenue_rub" name="Выручка" fill="#12805c" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <EmptyState message="Нет данных за выбранный период." />
            )}
          </div>

          <div className="ops-card mt-6 overflow-hidden p-0">
            <div className="border-b border-ops-border px-4 py-3">
              <h2 className="text-sm font-semibold">По моделям</h2>
            </div>
            {byModel.length === 0 ? (
              <div className="p-4">
                <EmptyState message="Нет агрегатов по моделям." />
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <th>Модель</th>
                      <th>События</th>
                      <th>Токены in/out</th>
                      <th>COGS</th>
                      <th>Выручка</th>
                      <th>Маржа</th>
                    </tr>
                  </thead>
                  <tbody>
                    {byModel.map((row) => (
                      <tr key={row.model_id}>
                        <td className="font-medium text-ops-text">{row.model_id}</td>
                        <td>{formatNumber(row.events)}</td>
                        <td>
                          {formatNumber(row.input_tokens)} / {formatNumber(row.output_tokens)}
                        </td>
                        <td>{formatRubPrecise(row.cost_rub)}</td>
                        <td>{formatRubPrecise(row.revenue_rub)}</td>
                        <td>
                          {formatRubPrecise(row.margin_rub)}
                          <span className="ml-2 text-ops-muted">
                            ({formatPercent(row.margin_percent)})
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </>
      ) : null}

      <div className="ops-card mt-6 overflow-hidden p-0">
        <div className="flex items-center justify-between border-b border-ops-border px-4 py-3">
          <h2 className="text-sm font-semibold">Журнал usage_ledger</h2>
          {ledgerQuery.data && ledgerQuery.data.items.length > 0 ? (
            <button
              type="button"
              className="ops-btn-secondary text-xs"
              onClick={() => exportUsageLedgerCsv(ledgerQuery.data!.items)}
            >
              {copy.billing.exportCsv}
            </button>
          ) : null}
        </div>
        {ledgerQuery.isLoading ? <LoadingState label="Загрузка журнала..." /> : null}
        {ledgerQuery.isError ? (
          <div className="p-4">
            <ErrorState message={copy.errors.loadUsage} onRetry={() => ledgerQuery.refetch()} />
          </div>
        ) : null}
        {ledgerQuery.data && ledgerQuery.data.items.length === 0 ? (
          <div className="p-4">
            <EmptyState message="Записей нет." />
          </div>
        ) : null}
        {ledgerQuery.data && ledgerQuery.data.items.length > 0 ? (
          <>
            <div className="overflow-x-auto">
              <table className="ops-table">
                <thead>
                  <tr>
                    <th>Дата</th>
                    <th>Пользователь</th>
                    <th>Модель</th>
                    <th>Источник</th>
                    <th>Токены</th>
                    <th>COGS</th>
                    <th>Кредиты</th>
                    <th>Выручка</th>
                    <th>Маржа</th>
                  </tr>
                </thead>
                <tbody>
                  {ledgerQuery.data.items.map((row) => (
                    <tr key={row.id}>
                      <td className="text-ops-muted">{formatDate(row.created_at)}</td>
                      <td>
                        <Link to={`/${scope}/users/${row.user_id}`} className="hover:underline">
                          {formatDisplayName(row.user_name)}
                        </Link>
                      </td>
                      <td>{row.model_id}</td>
                      <td>{usageSourceLabel(row.source)}</td>
                      <td>
                        {formatNumber(row.input_tokens)} / {formatNumber(row.output_tokens)}
                      </td>
                      <td>{formatRubPrecise(row.cost_rub)}</td>
                      <td>
                        {row.credits_charged > 0
                          ? `${formatNumber(row.credits_charged)} кр.`
                          : row.fair_use_used > 0
                            ? `${formatNumber(row.fair_use_used)} fair use`
                            : '0'}
                      </td>
                      <td>{formatRubPrecise(row.revenue_rub)}</td>
                      <td>
                        {formatRubPrecise(row.margin_rub)}
                        <span className="ml-1 text-ops-muted">
                          ({formatPercent(row.margin_percent)})
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="border-t border-ops-border">
              <CursorPagination
                pageNumber={pagination.pageNumber}
                itemCount={ledgerQuery.data.items.length}
                canPrev={pagination.canPrev}
                hasNext={Boolean(ledgerQuery.data.next_cursor)}
                loading={ledgerQuery.isFetching}
                onPrev={pagination.goPrev}
                onNext={() => pagination.goNext(ledgerQuery.data.next_cursor)}
              />
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
